// ============================================================================
// ToolCallCard — the tool-call pill shown in chat messages
// Extracted from MessageBubble so CardRenderer can reuse it as the graceful
// fallback for unknown / absent render payloads.
// ============================================================================

import { useState } from 'react'
import { Box, Typography, Collapse, IconButton } from '@mui/material'
import { ExpandMore, ExpandLess, Build } from '@mui/icons-material'
import { beoflowColors, typography, spacing, borderRadius } from '@/theme/muiTheme'
import { createColorGlass } from '@/theme/glassEffects'
import type { ToolCall } from '@/hooks/useChat'

const codeBlockSx = {
  fontFamily: typography.fontFamilyMonospace,
  fontSize: typography.fontSizeTiny,
  backgroundColor: 'rgba(0,0,0,0.2)',
  borderRadius: borderRadius.small,
  p: spacing.xs,
  m: 0,
  overflow: 'auto',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
}

export default function ToolCallCard({ toolCall, isStaff }: { toolCall: ToolCall; isStaff: boolean }) {
  const [expanded, setExpanded] = useState(false)

  const isInjected = toolCall.injected ?? false
  const accentColor = isInjected ? beoflowColors.graphGreyDark : beoflowColors.meanBlue
  const toolGlass = createColorGlass(accentColor, isInjected ? '08' : '0D')
  const isComplete = toolCall.status === 'complete'

  // Friendly, in-progress phrasing for builder tools (e.g. "Building widget…").
  const TOOL_LABELS: Record<string, { active: string; done: string }> = {
    create_widget: { active: 'Building widget', done: 'Built widget' },
    update_widget: { active: 'Updating widget', done: 'Updated widget' },
    delete_widget: { active: 'Deleting widget', done: 'Deleted widget' },
    list_widgets: { active: 'Searching widgets', done: 'Searched widgets' },
    get_widget: { active: 'Reading widget', done: 'Read widget' },
    add_widget_to_view: { active: 'Adding to view', done: 'Added to view' },
    remove_widget_from_view: { active: 'Removing from view', done: 'Removed from view' },
    rearrange_view: { active: 'Rearranging view', done: 'Rearranged view' },
    list_views: { active: 'Listing dashboards', done: 'Listed dashboards' },
    get_view: { active: 'Reading dashboard', done: 'Read dashboard' },
    create_view: { active: 'Creating dashboard', done: 'Created dashboard' },
    update_view: { active: 'Updating dashboard', done: 'Updated dashboard' },
    delete_view: { active: 'Deleting dashboard', done: 'Deleted dashboard' },
  }
  const labelPair = TOOL_LABELS[toolCall.tool]
  const displayName = labelPair
    ? (isComplete ? labelPair.done : labelPair.active)
    : toolCall.tool.replace(/_/g, ' ')

  return (
    <Box
      sx={{
        ...toolGlass,
        p: spacing.sm,
        my: spacing.xs,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: spacing.xs,
          cursor: isStaff ? 'pointer' : 'default',
        }}
        onClick={isStaff ? () => setExpanded(!expanded) : undefined}
      >
        <Build sx={{ fontSize: 14, color: accentColor }} />
        <Typography
          variant="monoSmall"
          sx={{ color: accentColor, flex: 1 }}
        >
          {isInjected ? 'ctx: ' : ''}{displayName}
          {!isComplete && ' ...'}
        </Typography>
        {/* Raw args/result inspection is a staff-only debug affordance. */}
        {isStaff && (
          <IconButton size="small" sx={{ p: 0 }}>
            {expanded ? (
              <ExpandLess sx={{ fontSize: 16, color: accentColor }} />
            ) : (
              <ExpandMore sx={{ fontSize: 16, color: accentColor }} />
            )}
          </IconButton>
        )}
      </Box>

      {isStaff && (
      <Collapse in={expanded}>
        <Box sx={{ mt: spacing.xs }}>
          {Object.keys(toolCall.args).length > 0 && (
            <Box sx={{ mb: spacing.xs }}>
              <Typography
                sx={{
                  fontFamily: typography.fontFamilyMonospace,
                  fontSize: typography.fontSizeTiny,
                  color: 'text.secondary',
                  mb: '2px',
                }}
              >
                Args:
              </Typography>
              <Box component="pre" sx={codeBlockSx}>
                {JSON.stringify(toolCall.args, null, 2)}
              </Box>
            </Box>
          )}
          {toolCall.result != null && (
            <Box>
              <Typography
                sx={{
                  fontFamily: typography.fontFamilyMonospace,
                  fontSize: typography.fontSizeTiny,
                  color: 'text.secondary',
                  mb: '2px',
                }}
              >
                Result:
              </Typography>
              <Box component="pre" sx={codeBlockSx}>
                {typeof toolCall.result === 'string'
                  ? toolCall.result
                  : JSON.stringify(toolCall.result, null, 2)}
              </Box>
            </Box>
          )}
        </Box>
      </Collapse>
      )}
    </Box>
  )
}
